import { useCallback, useEffect, useRef, useState } from "react";

type PaymentState = "PENDING" | "CONFIRMING" | "PAID" | "SETTLED" | "FAILED" | "UNKNOWN";

interface PaymentWSMessage {
  order_id: string;
  state: PaymentState;
  order_status: string;
  data: Record<string, any>;
  type?: string;
}

interface UsePaymentWSOptions {
  onStateChange?: (msg: PaymentWSMessage) => void;
  onCompleted?: (msg: PaymentWSMessage) => void;
  onFailed?: (msg: PaymentWSMessage) => void;
}

const TERMINAL_STATES: PaymentState[] = ["SETTLED", "FAILED"];
const WS_BASE_URL = import.meta.env.VITE_PAYMENT_WS_URL || import.meta.env.VITE_PAYMENT_URL?.replace("http", "ws");

export const usePaymentWebSocket = (options?: UsePaymentWSOptions) => {
  const [state, setState] = useState<PaymentState | null>(null);
  const [orderStatus, setOrderStatus] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const optionsRef = useRef(options);

  // Keep options ref up-to-date without causing reconnects
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const connect = useCallback((orderId: string) => {
    if (!orderId) return;

    // Close existing connection cleanly
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }

    // Small delay to ensure previous connection is fully torn down
    setTimeout(() => {
      const url = `${WS_BASE_URL}/v1/vms/payments/online/ws/${orderId}`;
      const ws = new WebSocket(url);

      ws.onopen = () => {
        setIsConnected(true);
      };

      ws.onmessage = (event) => {
        const msg: PaymentWSMessage = JSON.parse(event.data);
        if (msg.type === "pong") return;

        setState(msg.state);
        setOrderStatus(msg.order_status);
        optionsRef.current?.onStateChange?.(msg);

        if (msg.state === "PAID" || msg.state === "SETTLED") {
          optionsRef.current?.onCompleted?.(msg);
        }
        if (msg.state === "FAILED") {
          optionsRef.current?.onFailed?.(msg);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
      };

      ws.onerror = () => {
        setIsConnected(false);
      };

      wsRef.current = ws;
    }, 100);
  }, []); // No dependencies — stable reference

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    setState(null);
    setOrderStatus(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  return {
    connect,
    disconnect,
    state,
    orderStatus,
    isConnected,
    isTerminal: state ? TERMINAL_STATES.includes(state) : false,
  };
};
